import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { useParticipants } from './board-api';
import type { AssignmentDraft } from './types';

import styles from './Collab.module.css';

/**
 * Executor selector for task creation: Unassigned, Shared (for everyone who sees it) or specific
 * people of the board. Shared and unassigned are different states, never "an empty list".
 */
export function AssigneePicker({
  boardId,
  value,
  onChange,
  disabled,
}: {
  boardId: string;
  value: AssignmentDraft;
  onChange: (next: AssignmentDraft) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const participants = useParticipants(boardId, boardId !== '');
  return (
    <fieldset className={styles.group} disabled={disabled} data-testid="assignee-picker">
      <legend>{t('assignment.title')}</legend>
      <div className={styles.inline}>
        {(['unassigned', 'shared', 'assigned'] as const).map((kind) => (
          <label key={kind} className={styles.permission}>
            <input
              type="radio"
              name={`${id}-kind`}
              checked={value.kind === kind}
              onChange={() => {
                onChange({ kind, ids: kind === 'assigned' ? value.ids : [] });
              }}
            />
            <span>{t(`assignment.mode_${kind}`)}</span>
          </label>
        ))}
      </div>
      {value.kind === 'assigned' && (
        <ul className={styles.list} aria-label={t('assignment.people')}>
          {(participants.data ?? []).map((person) => (
            <li key={person.user_id}>
              <label className={styles.permission}>
                <input
                  type="checkbox"
                  checked={value.ids.includes(person.user_id)}
                  onChange={(event) => {
                    onChange({
                      kind: 'assigned',
                      ids: event.target.checked
                        ? [...value.ids, person.user_id]
                        : value.ids.filter((x) => x !== person.user_id),
                    });
                  }}
                />
                <span>{person.display_name}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}
